/**
 * Logica di validazione per l'assegnazione dei turni
 *
 * Regole:
 * - Esenzione notti: esclusi da TUTTE le notti (feriali e weekend)
 * - Medici con esenzione notti possono assolvere al debito weekend con turni diurni TI
 *   (a meno di esenzione attività cliniche)
 * - Esenzione attività cliniche: solo slot non clinici (ambulatoriali)
 * - Part-time / ore variabili: solo tramite debito orario settimanale in anagrafica (ore/giorno = debito÷5)
 * - Turno notte 20-8: esclude il medico da slot diurni del giorno ingresso e uscita
 * - TI notte weekend: chi fa notte TI sab/dom non fa notte TI anche gio/ven precedenti o lun/mar successivi
 * - Max debito settimanale (debito anagrafico × giorni lavorativi in settimana) — bypassabile con straordinario
 */

import { TIME_SLOTS, HOURS_PER_SLOT, HOURS_NIGHT_START_DAY, ISO_WEEK_MAX_CLINICAL_HOURS, ISO_WEEK_OVERTIME_HEADROOM_HOURS, getHoursForSlot, slotCategoryToDistKey } from './types.js';
import {
  getState,
  isDoctorAbsent,
  hasDesiderataBlock,
  hasSlotBlockForAbsence,
  getFullDayAbsenceDates,
  getContractDailyHoursForDoctor,
} from './store.js';
import { computeDoctorHoursForWeek, getFestiviCountInWeek } from './workload.js';
import { isDoctorCeased } from './utils.js';
import { getWeekStart } from './workload.js';

const DIURNAL_BANDS = new Set(['mattino', 'pomeriggio', 'prolungato']);
/** M, P, Pr, N clinici: incompatibili con slot Direzione (sempre Pr) lo stesso giorno. */
const CLINICAL_DAY_BANDS = new Set(['mattino', 'pomeriggio', 'prolungato', 'notte']);

/** Slot turno Direzione (categoria / tipo attività / nome standard). */
export function isDirezioneSlotKind(s) {
  if (!s) return false;
  const c = String(s.category ?? '').trim().toLowerCase();
  const at = String(s.activityType ?? s.activity_type ?? '').trim().toLowerCase();
  if (c === 'direzione' || at === 'direzione') return true;
  // Template predefinito: nome "Direzione" con categoria direzione; tollera maiuscole / API incoerenti
  const rawName = String(s.name ?? '').trim().toLowerCase();
  if (rawName === 'direzione') return true;
  return false;
}

function calendarDayKey(slot) {
  return String(slot?.date ?? '').slice(0, 10);
}

/**
 * Fascia oraria normalizzata per vincoli stesso giorno / stessa fascia (regola 7 e affini).
 * Slot Direzione = prolungato; se time_slot è vuoto in DB si assume prolungato.
 */
function plannerOverlapTimeBand(slot) {
  let raw = (slot?.timeSlot ?? slot?.time_slot ?? '').toString().trim().toLowerCase();
  if (!raw && isDirezioneSlotKind(slot)) {
    raw = 'prolungato';
  }
  return raw;
}

/**
 * Risolve doctorId per uno slot: solo chiave esatta (slotKey / id).
 * Non usare più un prefisso data-fascia-categoria-uoc-: SO1/SO2/Guardia (stesso UOS)
 * condividono quel prefisso e un solo incarico in mappa faceva "eredita" l'assegnazione
 * a tutti gli slot paralleli → falso "già in mattina/pomeriggio" per ogni medico.
 * Eccezione: slot Direzione, dove restano prefissi legacy per chiavi incoerenti.
 */
export function resolveAssignmentForSlot(s, byKey) {
  if (!s || !byKey) return undefined;
  const k = s.slotKey || s.id;
  if (k != null && String(k).trim() !== '' && byKey[k] != null && byKey[k] !== '') return byKey[k];

  const cache = assignmentFallbackCacheFor(byKey);
  const memoKey = k != null && String(k).trim() !== '' ? String(k) : null;
  if (cache && memoKey !== null && cache.bySlotKey.has(memoKey)) return cache.bySlotKey.get(memoKey);

  const resolved = resolveAssignmentFallback(s, byKey, cache);
  if (cache && memoKey !== null) cache.bySlotKey.set(memoKey, resolved);
  return resolved;
}

/**
 * Ramo lento: chiave esatta assente. Solo gli slot Direzione hanno un fallback (prefissi
 * legacy), e costa una scansione dell'intera mappa assegnazioni.
 */
function resolveAssignmentFallback(s, byKey, cache) {
  const d = calendarDayKey(s);
  if (!d) return undefined;
  const band = plannerOverlapTimeBand(s);
  const cat = String(s.category ?? '').trim().toLowerCase();
  const uoc = String(s.uocId ?? s.uoc_id ?? '').trim();

  if (!isDirezioneSlotKind(s)) return undefined;
  const bandD = band || 'prolungato';
  const prefixes = [
    `${d}-${bandD}-${cat}-${uoc}-`,
    `${d}-${bandD}-direzione-${uoc}-`,
    `${d}-${bandD}-direzione-`,
  ];
  const entries = assignedEntriesFor(byKey, cache);
  for (const pfx of prefixes) {
    for (let i = 0; i < entries.length; i++) {
      if (entries[i][0].startsWith(pfx)) return entries[i][1];
    }
  }
  return undefined;
}

/**
 * Memo del ramo lento di `resolveAssignmentForSlot`: la griglia del Piano Turni lo richiama
 * migliaia di volte sugli stessi slot (una volta per ogni medico di ogni tendina).
 *
 * Validità: **solo** per la mappa in stato (`state.shiftAssignmentsByKey`), che `setState`
 * ricostruisce sempre da zero — l'identità dell'oggetto è quindi già un token di versione.
 * Qualsiasi altra mappa (le copie simulate dei generatori, che possono essere mutate in place)
 * riceve `null` come cache e resta sul percorso non memoizzato: nessun rischio di valori stantii.
 * Invariante da mantenere: `state.shiftAssignmentsByKey` non va mai mutata in place.
 */
const assignmentFallbackMemo = new WeakMap();

function assignmentFallbackCacheFor(byKey) {
  if (typeof byKey !== 'object') return null;
  const known = assignmentFallbackMemo.get(byKey);
  if (known !== undefined) return known;
  // getState() solo alla prima occorrenza di ogni mappa, non a ogni risoluzione.
  const cache = byKey === getState()?.shiftAssignmentsByKey
    ? { bySlotKey: new Map(), assignedEntries: null }
    : null;
  assignmentFallbackMemo.set(byKey, cache);
  return cache;
}

/** Coppie [chiave, medico] con assegnazione non vuota, nell'ordine di inserimento della mappa. */
function assignedEntriesFor(byKey, cache) {
  if (cache?.assignedEntries) return cache.assignedEntries;
  const entries = Object.entries(byKey).filter(([, docId]) => docId != null && docId !== '');
  if (cache) cache.assignedEntries = entries;
  return entries;
}

/**
 * Stesso giorno, slot diversi: incompatibilità per fascia uguale oppure Direzione vs M/P/Pr/N.
 */
function sameDaySlotTimeExcludes(s, slot, slotKeyOrId) {
  if (calendarDayKey(s) !== calendarDayKey(slot) || slotKeyOrId(s) === slotKeyOrId(slot)) return false;
  const bandS = plannerOverlapTimeBand(s);
  const bandHere = plannerOverlapTimeBand(slot);
  if (bandS === bandHere) return true;
  const dirS = isDirezioneSlotKind(s);
  const dirHere = isDirezioneSlotKind(slot);
  if (dirHere && !dirS && CLINICAL_DAY_BANDS.has(bandS)) return true;
  if (dirS && !dirHere && CLINICAL_DAY_BANDS.has(bandHere)) return true;
  if (dirHere && dirS) return true;
  return false;
}

function bandToItalianLabel(band) {
  if (band === 'mattino') return 'mattino';
  if (band === 'pomeriggio') return 'pomeriggio';
  if (band === 'prolungato') return 'prolungato';
  if (band === 'notte') return 'notte';
  return band || 'questa fascia';
}

/**
 * Estrae i campi minimi da slotKey (`YYYY-MM-DD-timeSlot-category-...`) per
 * intercettare conflitti anche quando lo slot non è più nel calendario visibile
 * ma è ancora presente tra le assegnazioni persistite nel DB.
 */
function parseSlotFromKey(slotKey) {
  if (!slotKey || typeof slotKey !== 'string') return null;
  const m = slotKey.match(/^(\d{4}-\d{2}-\d{2})-([a-z_]+)-([a-z_]+)-/i);
  if (!m) return null;
  return {
    slotKey,
    id: slotKey,
    date: m[1],
    timeSlot: m[2],
    category: m[3],
    activityType: 'clinico',
    name: '',
  };
}

/** Calendario solare affidabile vs `Date(iso)` (evita cambi giorno per fuso/DST). */
function addCalendarDays(isoDay, deltaDays) {
  const x = new Date(`${isoDay}T12:00:00`);
  x.setDate(x.getDate() + deltaDays);
  return x.toISOString().slice(0, 10);
}

function parsedKeyIsClinicalNightParsed(p) {
  return !!(p && p.timeSlot === TIME_SLOTS.NOTTE && (!p.activityType || p.activityType !== 'reperibilita'));
}

/**
 * Verifica se un medico può essere assegnato a uno slot
 * @param {string} doctorId
 * @param {object} slot - slot da calendario (date, timeSlot, weekday, slotKey, ...)
 * @param {object} options - { slots, assignmentsByKey, allowStraordinario }
 */
export function canAssignDoctorToSlot(doctorId, slot, options = {}) {
  if (!slot || !slot.date) return { ok: false, reason: 'Slot non valido' };
  const state = getState();
  const { slots = [], assignmentsByKey = state.shiftAssignmentsByKey || {}, allowStraordinario = false } = typeof options === 'object' ? options : {};
  const doctor = state.doctors.find(d => String(d.id) === String(doctorId));
  const slotDate = new Date(slot.date);

  if (!doctor) return { ok: false, reason: 'Medico non trovato' };

  // 0. Rapporto cessato: non assegnabile per turni successivi all'ultimo giorno
  // lavorato. I turni fino a quella data restano validi e visibili nello storico.
  if (isDoctorCeased(doctor, slot.date)) {
    return {
      ok: false,
      reason: `Rapporto cessato il ${doctor.dataFineRapporto}: non assegnabile da questa data in poi`,
      reasonCode: 'doctor_ceased',
    };
  }

  // Deroga manuale (con conferma) per assenze/desiderata: vale per slot clinici M/P/Pr/N
  // e per la reperibilità. Confermando, la UI rimuove dal piano assenze il giorno/desiderata
  // in conflitto. (Le altre esclusioni — esenzioni, specializzando, 0% — restano hard.)
  const allowAbsenceDesiderataManualOverride = options.allowAbsenceDesiderataManualOverride === true;

  // 1. Assenza giustificata (piano assenze)
  if (isDoctorAbsent(doctorId, slot.date)) {
    if (!allowAbsenceDesiderataManualOverride) {
      return { ok: false, reason: 'Medico assente in questa data', reasonCode: 'absence_desiderata_override' };
    }
  }

  // 2. Desiderata
  if (hasDesiderataBlock(doctorId, slot.date, slot.timeSlot)) {
    if (!allowAbsenceDesiderataManualOverride) {
      return { ok: false, reason: 'Desiderata: medico escluso da questo slot', reasonCode: 'absence_desiderata_override' };
    }
  }

  // 2b. Libera Professione / Formazione (esclusione selettiva slot)
  if (hasSlotBlockForAbsence(doctorId, slot.date, slot.timeSlot)) {
    if (!allowAbsenceDesiderataManualOverride) {
      return { ok: false, reason: 'Indisponibile (Libera Professione/Formazione) per questo slot', reasonCode: 'absence_desiderata_override' };
    }
  }

  // 3. Esenzione notti e reperibilità: esclusi da notti E da turni di reperibilità
  const isReperibilitaSlot = slot.timeSlot === TIME_SLOTS.REPERIBILITA_NOTTE || slot.timeSlot === TIME_SLOTS.REPERIBILITA_GIORNO || slot.activityType === 'reperibilita';
  if (doctor.esenzioneNotti && (slot.timeSlot === TIME_SLOTS.NOTTE || isReperibilitaSlot)) {
    return { ok: false, reason: 'Medico esente da notti e reperibilità' };
  }
  // 3a. Esenzione weekend: sab/dom diurni+notte + TI diurna nei festivi infrasettimanali (no reperibilità)
  const isTiDiurnoFestivoInfra =
    !!slot.isFestivoInfrasettimanale &&
    slot.category === 'terapia_intensiva' &&
    (slot.timeSlot === TIME_SLOTS.MATTINO || slot.timeSlot === TIME_SLOTS.POMERIGGIO || slot.timeSlot === TIME_SLOTS.PROLUNGATO);
  const isWeekendTurno = (slot.weekday === 'sab' || slot.weekday === 'dom') && !isReperibilitaSlot;
  if (doctor.esenzioneWeekend && (isWeekendTurno || isTiDiurnoFestivoInfra)) {
    return { ok: false, reason: 'Medico esente da turni weekend e da TI diurna nei festivi infrasettimanali' };
  }

  // 3b. Specializzando: escluso da notti, festivi e reperibilità
  if (doctor.specializzando) {
    const isNotte = slot.timeSlot === TIME_SLOTS.NOTTE;
    if (isNotte) return { ok: false, reason: 'Specializzando: non assegnabile a turni notturni' };
    if (isReperibilitaSlot) return { ok: false, reason: 'Specializzando: non assegnabile a reperibilità' };
    const isFestivo = slot.weekday === 'sab' || slot.weekday === 'dom' || slot.isFestivoInfrasettimanale;
    if (isFestivo) return { ok: false, reason: 'Specializzando: non assegnabile a turni festivi' };
  }

  // 4. Esenzione attività cliniche
  if (doctor.esenzioneAttivitaCliniche && slot.activityType === 'clinico') {
    return { ok: false, reason: 'Medico esente da attività cliniche (solo ambulatoriali)' };
  }

  const allowManualOverride = options.allowManualOverride === true;
  const allowOverrideZeroPercent = options.allowOverrideZeroPercent === true; // Solo per inserimento manuale
  const allowOverrideMaxDays = options.allowOverrideMaxDays === true;
  // Competenza Direzione = solo flag `abilitaDirezione` (non le barre di ripartizione).
  // Solver: mai allowDirezioneManualOverride → esclusi chi non ha il flag.
  // Manuale: solo dopo conferma esplicita (ShiftPlanner passa allowDirezioneManualOverride: true).
  const allowDirezioneManualOverride = options.allowDirezioneManualOverride === true;
  /** Solo dopo conferma UI: P o N clinico il giorno dopo una notte clinica (smonto). */
  const allowNightRestSmontoManualOverride = options.allowNightRestSmontoManualOverride === true;
  /** Solo dopo conferma UI: notte assegnata a chi fa notte il giorno prima/dopo (notti consecutive). */
  const allowNightConsecutiveManualOverride = options.allowNightConsecutiveManualOverride === true;
  const isRepNight = (s) => (s.timeSlot === TIME_SLOTS.REPERIBILITA_NOTTE) || (s.timeSlot === TIME_SLOTS.NOTTE && s.activityType === 'reperibilita');
  const isClinicalNight = (s) => s.timeSlot === TIME_SLOTS.NOTTE && s.activityType !== 'reperibilita';

  // 5. Ripartizione 0%: ESCLUSIONE ASSOLUTA per algoritmo automatico; manuale può forzare (allowOverrideZeroPercent)
  // Eccezione TI: 0% in terapia_intensiva esonera solo da mattino/pomeriggio feriali, NON da notti né weekend TI
  if (!allowOverrideZeroPercent) {
    const distKey = slotCategoryToDistKey(slot.category, slot) || (slot.uocId === 'uos_medicina_dolore' ? 'medicina_dolore' : null);
    const pct = distKey != null ? doctor.distribuzione?.[distKey] : undefined;
    if (pct !== undefined && pct === 0) {
      const isTI = slot.category === 'terapia_intensiva';
      const isTINightOrWeekend = isTI && (slot.timeSlot === TIME_SLOTS.NOTTE || ['sab', 'dom'].includes(slot.weekday));
      if (!isTINightOrWeekend) {
        return { ok: false, reason: 'Medico escluso da quest\'area (0% nella ripartizione)' };
      }
    }
  }

  // 6. Abilità Direzione (unico criterio "competenza" per slot Direzione; senza flag = come 0% per il solver)
  if (isDirezioneSlotKind(slot) && !doctor.abilitaDirezione && !allowDirezioneManualOverride) {
    return {
      ok: false,
      reason: 'Medico senza Abilità Direzione (non assegnabile automaticamente a questo turno)',
      reasonCode: 'direzione_not_enabled',
    };
  }
  const allSlots = slots.length ? slots : (state.shiftSlots || []);
  const byKey = assignmentsByKey;

  // Medici con abilità Direzione: no notti e no weekend per slot clinici TI/SO (solo stesura automatica; manuale può forzare)
  // allowDirectorTamponage: consente Direttori quando tutti i non-Direttori hanno >=40h (tamponaggio per evitare straordinari)
  const allowDirectorTamponage = options.allowDirectorTamponage === true;
  const isClinicalTIorSO = (slot.category === 'terapia_intensiva' || slot.category === 'sala_operatoria' || slot.category === 'nora') && slot.activityType !== 'reperibilita';
  const isNightOrWeekend = slot.timeSlot === TIME_SLOTS.NOTTE || ['sab', 'dom'].includes(slot.weekday);
  if (!allowManualOverride && !allowDirectorTamponage && doctor.abilitaDirezione && isClinicalTIorSO && isNightOrWeekend) {
    const nonDirEligible = state.doctors.filter(d => !d.abilitaDirezione)
      .some(d => canAssignDoctorToSlot(d.id, slot, { slots: allSlots, assignmentsByKey: byKey }).ok);
    if (nonDirEligible) {
      return { ok: false, reason: 'Medico Direzione: no notti/weekend (solo in carenza)' };
    }
  }

  // 7. Un medico non può fare due turni nella stessa fascia oraria dello stesso giorno (es. TI mattino + SO mattino)
  const getAssigned = (s) => resolveAssignmentForSlot(s, byKey);
  const slotKeyOrId = (s) => s.slotKey || s.id;
  const bandHere = plannerOverlapTimeBand(slot);

  // Escludi lo slot corrente per evitare falsi positivi (es. simulazione con assegnazione già inclusa)
  const sameDayConflicts = allSlots.filter(s => sameDaySlotTimeExcludes(s, slot, slotKeyOrId));
  const conflictingAssigned = sameDayConflicts.filter(s => getAssigned(s) === doctorId);
  // Fallback: include assegnazioni "orfane" (slot presenti in DB ma non nel calendario corrente).
  // Senza questo, la tendina può mostrare un medico selezionabile ma il backend rifiuta con 409.
  if (conflictingAssigned.length === 0) {
    const currentSlotKey = slotKeyOrId(slot);
    for (const [assignedKey, assignedDoctorId] of Object.entries(byKey || {})) {
      if (String(assignedDoctorId) !== String(doctorId)) continue;
      if (String(assignedKey) === String(currentSlotKey)) continue;
      const parsed = parseSlotFromKey(assignedKey);
      if (!parsed) continue;
      if (sameDaySlotTimeExcludes(parsed, slot, slotKeyOrId)) {
        conflictingAssigned.push(parsed);
        break;
      }
    }
  }
  if (conflictingAssigned.length > 0) {
    const slotLabel = bandToItalianLabel(bandHere);
    const involvesDir = isDirezioneSlotKind(slot) || conflictingAssigned.some(s => isDirezioneSlotKind(s));
    const reason = involvesDir
      ? 'Medico già assegnato lo stesso giorno a un turno incompatibile (Direzione è Pr e non combina con M, P, Pr o N clinici, né con un secondo slot Direzione)'
      : `Medico già assegnato a un turno ${slotLabel} in questa data`;
    return { ok: false, reason };
  }

  // 7c. Vietare M o P se il medico ha già un P o M in quella giornata e ha ancora giorni liberi nella settimana (per favorire livellamento giorni: evitare 12h in un giorno quando si potrebbero usare giorni distinti). Non si applica a weekend (sab/dom) dove il doppio turno TI è obbligatorio. Si può derogare: livellamento (allowOverrideSameDayMPForLeveling) o assegnazione manuale (allowManualOverride).
  const allowOverrideSameDayMP = options.allowOverrideSameDayMPForLeveling === true || options.allowManualOverride === true;
  const isMPSlot = bandHere === 'mattino' || bandHere === 'pomeriggio';
  const isWeekendSlot = slot.weekday === 'sab' || slot.weekday === 'dom';
  if (isMPSlot && !allowOverrideSameDayMP && !isWeekendSlot) {
    const sameDayMP = allSlots.filter(
      s => s.date === slot.date && (plannerOverlapTimeBand(s) === 'mattino' || plannerOverlapTimeBand(s) === 'pomeriggio') && slotKeyOrId(s) !== slotKeyOrId(slot)
    );
    const alreadyHasMPOnDate = sameDayMP.some(s => getAssigned(s) === doctorId);
    if (alreadyHasMPOnDate) {
      const weekStart = getWeekStart(slot.date);
      const [year] = slot.date.split('-').map(Number);
      const workingDaysOfWeek = Math.max(0, 5 - getFestiviCountInWeek(weekStart, year));
      const isRep = (s) => s.activityType === 'reperibilita' || s.timeSlot === TIME_SLOTS.REPERIBILITA_NOTTE || s.timeSlot === TIME_SLOTS.REPERIBILITA_GIORNO;
      const doctorWorkingDatesInWeek = new Set();
      for (const s of allSlots) {
        if (getWeekStart(s.date) !== weekStart || getAssigned(s) !== doctorId || isRep(s)) continue;
        doctorWorkingDatesInWeek.add(s.date);
      }
      const doctorWorkingDays = doctorWorkingDatesInWeek.size;
      if (doctorWorkingDays < workingDaysOfWeek) {
        return { ok: false, reason: 'Vietato secondo turno M/P nella stessa giornata se ci sono ancora giorni liberi nella settimana (livellamento giorni)' };
      }
    }
  }

  // 7d. Weekend TI M/P stesso medico:
  // criterio di qualità del solver, NON vincolo hard in assegnazione manuale.

  // 8. Turno notte: esclusione giorno ingresso (20) e uscita (8)
  if (isClinicalNight(slot)) {
    const dayStart = slot.date;
    const nextDay = new Date(slotDate);
    nextDay.setDate(nextDay.getDate() + 1);
    const dayEnd = nextDay.toISOString().slice(0, 10);
    const prevDay = new Date(slotDate);
    prevDay.setDate(prevDay.getDate() - 1);
    const dayPrev = prevDay.toISOString().slice(0, 10);

    const slotKey = slot.slotKey || slot.id;
    // Seconda notte clinica lo STESSO giorno: sempre vietata (impossibile fisicamente).
    const hasNightSameDay = allSlots
      .filter(s => isClinicalNight(s) && s.date === dayStart && (s.slotKey || s.id) !== slotKey)
      .some(s => getAssigned(s) === doctorId);
    if (hasNightSameDay) {
      return { ok: false, reason: 'Turno notte: medico già impegnato in un\'altra notte nello stesso giorno' };
    }
    // Notte il giorno precedente (D-1) o successivo (D+1): derogabile in manuale con conferma.
    const hasNightAdjacentDay = allSlots
      .filter(s => isClinicalNight(s) && (s.date === dayPrev || s.date === dayEnd) && (s.slotKey || s.id) !== slotKey)
      .some(s => getAssigned(s) === doctorId);
    if (hasNightAdjacentDay && !allowNightConsecutiveManualOverride) {
      return {
        ok: false,
        reason: 'Turno notte: il medico fa notte il giorno precedente o successivo (conferma per assegnare comunque)',
        reasonCode: 'night_consecutive',
      };
    }

    // 8b. Notte 20-8: medico non può avere mattino/pomeriggio/prolungato il giorno ingresso (20) né uscita (8) - nessun riposo
    const diurnalSlots = allSlots.filter(s =>
      (s.timeSlot === TIME_SLOTS.MATTINO || s.timeSlot === TIME_SLOTS.POMERIGGIO || s.timeSlot === TIME_SLOTS.PROLUNGATO) && (s.date === dayStart || s.date === dayEnd)
    );
    let hasDiurnalSamePeriod = diurnalSlots.some(s => getAssigned(s) === doctorId);
    if (!hasDiurnalSamePeriod) {
      const startD = slot.date;
      const endD = dayEnd;
      for (const [k, assignedId] of Object.entries(byKey || {})) {
        if (String(assignedId) !== String(doctorId)) continue;
        const p = parseSlotFromKey(k);
        if (!p || (p.date !== startD && p.date !== endD)) continue;
        if (p.timeSlot === TIME_SLOTS.MATTINO || p.timeSlot === TIME_SLOTS.POMERIGGIO || p.timeSlot === TIME_SLOTS.PROLUNGATO) {
          hasDiurnalSamePeriod = true;
          break;
        }
      }
    }
    if (hasDiurnalSamePeriod) {
      return { ok: false, reason: 'Turno notte: medico già impegnato in turno diurno il giorno ingresso o uscita (nessun riposo)' };
    }

    // 8c. Congedo/permesso/formazione giorno D: vieta notte del giorno precedente (D-1), che termina alle 8 del giorno D
    const absenceNextDay = getFullDayAbsenceDates(doctorId, dayEnd, dayEnd);
    if (absenceNextDay.size > 0 && !allowAbsenceDesiderataManualOverride) {
      return {
        ok: false,
        reason: 'Turno notte: medico ha assenza giustificata (congedo/permesso/formazione) il giorno uscita (8)',
        reasonCode: 'absence_desiderata_override',
      };
    }
  }

  // 9. Se il medico ha un turno notte (clinica):
  // - escluso da M/P/Pr nello stesso giorno
  // - giorno successivo: mattino e prolungato sempre vietati; P o N clinico in smonto — manuale con conferma (reasonCode night_rest_smonto)
  // NB: la reperibilità (giorno o notte) NON è un turno "vero" e non rientra
  // nel riposo post-notte: chi fa notte D può essere reperibile D+1.
  const nightSlots = allSlots.filter(s => isClinicalNight(s));
  for (const ns of nightSlots) {
    if (getAssigned(ns) !== doctorId) continue;
    const nextDayStr = addCalendarDays(ns.date, 1);
    if (slot.date === ns.date && (slot.timeSlot === TIME_SLOTS.MATTINO || slot.timeSlot === TIME_SLOTS.POMERIGGIO || slot.timeSlot === TIME_SLOTS.PROLUNGATO)) {
      return { ok: false, reason: 'Medico già in turno notte: escluso da slot diurni dello stesso giorno' };
    }
    if (slot.date === nextDayStr) {
      // Reperibilità: nessun vincolo post-notte (non è un turno "vero").
      if (isReperibilitaSlot) continue;
      const nextDayHard = slot.timeSlot === TIME_SLOTS.MATTINO || slot.timeSlot === TIME_SLOTS.PROLUNGATO;
      const nextDaySmontoPN = slot.timeSlot === TIME_SLOTS.POMERIGGIO || isClinicalNight(slot);
      if (nextDayHard) {
        return { ok: false, reason: 'Medico già in turno notte: escluso da mattino o prolungato il giorno successivo (riposo post-notte)' };
      }
      if (nextDaySmontoPN) {
        // Notte dopo notte (clinica): coperta anche dalla conferma "notti consecutive".
        const smontoAllowedHere = allowNightRestSmontoManualOverride
          || (isClinicalNight(slot) && allowNightConsecutiveManualOverride);
        if (!smontoAllowedHere) {
          return {
            ok: false,
            reason: 'Medico in smonto post-notte: P o N richiedono conferma per assegnazione manuale',
            reasonCode: 'night_rest_smonto',
          };
        }
        continue;
      }
      return { ok: false, reason: 'Medico già in turno notte: escluso da questo turno il giorno successivo (riposo post-notte)' };
    }
  }

  const priorNightDateStr = addCalendarDays(slot.date, -1);
  const blocksNextCalendarDayAfterNight = (
    slot.timeSlot === TIME_SLOTS.MATTINO ||
    slot.timeSlot === TIME_SLOTS.POMERIGGIO ||
    slot.timeSlot === TIME_SLOTS.PROLUNGATO ||
    isClinicalNight(slot)
  );
  if (blocksNextCalendarDayAfterNight) {
    for (const [key, assignedId] of Object.entries(byKey || {})) {
      if (assignedId == null || assignedId === '') continue;
      if (String(assignedId) !== String(doctorId)) continue;
      const p = parseSlotFromKey(key);
      if (!p || !parsedKeyIsClinicalNightParsed(p)) continue;
      if (p.date !== priorNightDateStr) continue;
      const nextDayHard = slot.timeSlot === TIME_SLOTS.MATTINO || slot.timeSlot === TIME_SLOTS.PROLUNGATO;
      const nextDaySmontoPN = slot.timeSlot === TIME_SLOTS.POMERIGGIO || isClinicalNight(slot);
      if (nextDayHard) {
        return { ok: false, reason: 'Medico già in turno notte: escluso da mattino o prolungato il giorno successivo (riposo post-notte)' };
      }
      if (nextDaySmontoPN) {
        // Notte dopo notte (clinica): coperta anche dalla conferma "notti consecutive".
        const smontoAllowedHere = allowNightRestSmontoManualOverride
          || (isClinicalNight(slot) && allowNightConsecutiveManualOverride);
        if (!smontoAllowedHere) {
          return {
            ok: false,
            reason: 'Medico in smonto post-notte: P o N richiedono conferma per assegnazione manuale',
            reasonCode: 'night_rest_smonto',
          };
        }
        continue;
      }
      return { ok: false, reason: 'Medico già in turno notte: escluso da questo turno il giorno successivo (riposo post-notte)' };
    }
  }

  // 9b. Mai due notti TI consecutive (giorni di calendario)
  const isTINightSlot = slot.timeSlot === TIME_SLOTS.NOTTE && slot.category === 'terapia_intensiva';
  if (isTINightSlot) {
    const addDays = (d, n) => {
      const x = new Date(d + 'T12:00:00');
      x.setDate(x.getDate() + n);
      return x.toISOString().slice(0, 10);
    };
    const prevDay = addDays(slot.date, -1);
    const nextDay = addDays(slot.date, 1);
    const tiNightSlotsConsec = allSlots.filter(
      s => s.timeSlot === TIME_SLOTS.NOTTE && s.category === 'terapia_intensiva' && (s.date === prevDay || s.date === nextDay)
    );
    const hasConsecutiveTINight = tiNightSlotsConsec.some(s => getAssigned(s) === doctorId);
    if (hasConsecutiveTINight && !allowNightConsecutiveManualOverride) {
      return {
        ok: false,
        reason: 'Non sono ammesse due notti TI consecutive (conferma per assegnare comunque)',
        reasonCode: 'night_consecutive',
      };
    }
  }

  // 10. TI notte weekend (distanza infrasettimanale ↔ weekend): solo solver / controlli automatici;
  // in assegnazione manuale (allowManualOverride) non si blocca.
  if (!allowManualOverride) {
    const isTINight = isTINightSlot;
    if (isTINight) {
      const weekStart = getWeekStart(slot.date);
      const addDays = (d, n) => {
        const x = new Date(d);
        x.setDate(x.getDate() + n);
        return x.toISOString().slice(0, 10);
      };
      const forbiddenDates = new Set();
      if (['sab', 'dom'].includes(slot.weekday)) {
        // Chi fa notte TI sab/dom: non può avere notte TI anche gio/ven stessa settimana o lun/mar successivi
        const mon = new Date(weekStart + 'T12:00:00');
        forbiddenDates.add(addDays(mon, 3)); // gio
        forbiddenDates.add(addDays(mon, 4)); // ven
        forbiddenDates.add(addDays(mon, 7)); // lun successivo
        forbiddenDates.add(addDays(mon, 8)); // mar successivo
      } else if (['gio', 'ven'].includes(slot.weekday)) {
        // Chi fa notte TI gio/ven: non può avere notte TI sab/dom stessa settimana
        const mon = new Date(weekStart + 'T12:00:00');
        forbiddenDates.add(addDays(mon, 5)); // sab
        forbiddenDates.add(addDays(mon, 6)); // dom
      } else if (['lun', 'mar'].includes(slot.weekday)) {
        // Chi fa notte TI lun/mar: non può avere notte TI sab/dom settimana precedente
        const mon = new Date(weekStart + 'T12:00:00');
        forbiddenDates.add(addDays(mon, -2)); // sab precedente
        forbiddenDates.add(addDays(mon, -1)); // dom precedente
      }
      const tiNightSlots = allSlots.filter(s => s.timeSlot === TIME_SLOTS.NOTTE && s.category === 'terapia_intensiva');
      const hasForbiddenTINight = tiNightSlots.some(s => forbiddenDates.has(s.date) && getAssigned(s) === doctorId);
      if (hasForbiddenTINight) {
        return { ok: false, reason: 'TI notte weekend: non compatibile con notte TI gio/ven o lun/mar adiacenti' };
      }
    }
  }

  // 11. Reperibilità notte vs turno notte:
  // compatibilità consentita su giorni adiacenti; blocco solo su stessa data.
  if (isRepNight(slot)) {
    const nightSlotsRep = allSlots.filter(s => {
      if (!isClinicalNight(s)) return false;
      return s.date === slot.date;
    });
    const hasNightShift = nightSlotsRep.some(s => getAssigned(s) === doctorId);
    if (hasNightShift) return { ok: false, reason: 'Reperibile notte: non compatibile con turno notte' };
  }
  // Reperibilità giorno: chi è reperibile 8-20 sab/dom non può essere in turno (mattino/pomeriggio) stesso giorno
  if (slot.timeSlot === TIME_SLOTS.REPERIBILITA_GIORNO) {
    const daySlots = allSlots.filter(s => (s.timeSlot === TIME_SLOTS.MATTINO || s.timeSlot === TIME_SLOTS.POMERIGGIO || s.timeSlot === TIME_SLOTS.PROLUNGATO) && s.date === slot.date);
    const hasDayShift = daySlots.some(s => getAssigned(s) === doctorId);
    if (hasDayShift) return { ok: false, reason: 'Reperibile giorno: non compatibile con turno stesso giorno' };
  }
  // Turno regolare: chi è reperibile notte non può fare turno notte stessa data
  if (isClinicalNight(slot)) {
    const repNotteSlots = allSlots.filter(s => isRepNight(s));
    const hasRepNotte = repNotteSlots.some(s => {
      if (getAssigned(s) !== doctorId) return false;
      return s.date === slot.date;
    });
    if (hasRepNotte) return { ok: false, reason: 'Medico già reperibile notte: non può fare turno notte' };
  }
  // Turno mattino/pomeriggio/prolungato: chi è reperibile giorno non può fare turno nello stesso giorno
  if (slot.timeSlot === TIME_SLOTS.MATTINO || slot.timeSlot === TIME_SLOTS.POMERIGGIO || slot.timeSlot === TIME_SLOTS.PROLUNGATO) {
    const repGiornoSlots = allSlots.filter(s => s.timeSlot === TIME_SLOTS.REPERIBILITA_GIORNO && s.date === slot.date);
    const hasRepGiorno = repGiornoSlots.some(s => getAssigned(s) === doctorId);
    if (hasRepGiorno) return { ok: false, reason: 'Medico già reperibile giorno: non può fare turno stesso giorno' };
  }

  // 12a. Max 1 notte e max 2 reperibilità per medico per settimana ISO
  const weekStartForCap = getWeekStart(slot.date);
  const isNightSlot = isClinicalNight(slot);
  const isRepSlot = slot.timeSlot === TIME_SLOTS.REPERIBILITA_NOTTE || slot.timeSlot === TIME_SLOTS.REPERIBILITA_GIORNO || slot.activityType === 'reperibilita';
  if (!allowManualOverride && (isNightSlot || isRepSlot)) {
    const alreadyAssignedToThisDoctor = getAssigned(slot) === doctorId;
    let nightsInWeek = 0;
    let repInWeek = 0;
    for (const s of allSlots) {
      if (getWeekStart(s.date) !== weekStartForCap || getAssigned(s) !== doctorId) continue;
      if (s.timeSlot === TIME_SLOTS.NOTTE) nightsInWeek++;
      else if (s.timeSlot === TIME_SLOTS.REPERIBILITA_NOTTE || s.timeSlot === TIME_SLOTS.REPERIBILITA_GIORNO || s.activityType === 'reperibilita') repInWeek++;
    }
    if (isNightSlot) {
      const wouldHaveNights = nightsInWeek + (alreadyAssignedToThisDoctor ? 0 : 1);
      if (wouldHaveNights > 1) {
        return { ok: false, reason: 'Max 1 notte per settimana ISO' };
      }
    }
    if (isRepSlot) {
      const wouldHaveRep = repInWeek + (alreadyAssignedToThisDoctor ? 0 : 1);
      if (wouldHaveRep > 2) {
        return { ok: false, reason: 'Max 2 reperibilità per settimana ISO' };
      }
    }
  }

  // 12b. Max 5 giorni lavorativi/settimana (2 giorni liberi). Giornata intera formazione (7,6h) e Congedo/Permesso/Rischio valgono come giorno. Formazione a ore no. Reperibilità non contano come giorno.
  const maxDaysPerWeek = options.maxDaysPerWeek;
  const isRepSlotBeingAssigned = slot.activityType === 'reperibilita' || slot.timeSlot === TIME_SLOTS.REPERIBILITA_NOTTE || slot.timeSlot === TIME_SLOTS.REPERIBILITA_GIORNO;
  if (!allowManualOverride && !allowOverrideMaxDays && !isRepSlotBeingAssigned && maxDaysPerWeek !== undefined && maxDaysPerWeek > 0) {
    const weekStart = getWeekStart(slot.date);
    const weekEnd = new Date(weekStart + 'T12:00:00');
    weekEnd.setDate(weekEnd.getDate() + 6);
    const weekEndStr = weekEnd.toISOString().slice(0, 10);
    const doctorDays = new Set();
    for (const s of allSlots) {
      if (getWeekStart(s.date) !== weekStart || getAssigned(s) !== doctorId) continue;
      const isRepSlot = s.timeSlot === TIME_SLOTS.REPERIBILITA_NOTTE || s.timeSlot === TIME_SLOTS.REPERIBILITA_GIORNO || s.activityType === 'reperibilita';
      if (isRepSlot) continue;
      doctorDays.add(s.date);
      if (s.timeSlot === TIME_SLOTS.NOTTE) {
        const nextDay = new Date(s.date + 'T12:00:00');
        nextDay.setDate(nextDay.getDate() + 1);
        const nextStr = nextDay.toISOString().slice(0, 10);
        if (nextStr >= weekStart && nextStr <= weekEndStr) doctorDays.add(nextStr);
      }
    }
    for (const d of getFullDayAbsenceDates(doctorId, weekStart, weekEndStr)) doctorDays.add(d);
    const isNewDay = !doctorDays.has(slot.date);
    if (isNewDay && doctorDays.size >= maxDaysPerWeek) {
      return { ok: false, reason: `Medico già a ${maxDaysPerWeek} giorni in questa settimana` };
    }
  }

  // 12. Vincolo ore settimanali vs debito anagrafico (ore/giorno = debito settimanale ÷ 5).
  const isReperibilita = slot.activityType === 'reperibilita' || slot.timeSlot === TIME_SLOTS.REPERIBILITA_NOTTE || slot.timeSlot === TIME_SLOTS.REPERIBILITA_GIORNO;
  const isFestivoTI = slot.isFestivoInfrasettimanale && slot.category === 'terapia_intensiva' && (slot.timeSlot === TIME_SLOTS.MATTINO || slot.timeSlot === TIME_SLOTS.POMERIGGIO || slot.timeSlot === TIME_SLOTS.PROLUNGATO);
  // Solo un override manuale esplicito ignora il monte ore. Lo straordinario non azzera
  // il tetto: concede il margine ISO_WEEK_OVERTIME_HEADROOM_HOURS sopra il debito, come
  // il solver (full-time 38h + 4h = 42h fisiche). Con debito 0 non c'è monte ore su cui
  // fare straordinario, quindi il tetto resta 0 e nessuno slot è assegnabile.
  if (!allowManualOverride && !isReperibilita && !isFestivoTI) {
    const contractDailyHours = getContractDailyHoursForDoctor(doctor);
    const [year] = slot.date.split('-').map(Number);
    const weekStart = getWeekStart(slot.date);
    const festiviCount = getFestiviCountInWeek(weekStart, year);
    const workingDays = Math.max(0, 5 - festiviCount);
    const debitoBase = contractDailyHours * workingDays;
    const debito = (allowStraordinario && debitoBase > 0)
      ? Math.min(ISO_WEEK_MAX_CLINICAL_HOURS, debitoBase + ISO_WEEK_OVERTIME_HEADROOM_HOURS)
      : debitoBase;
    const weekHours = computeDoctorHoursForWeek(allSlots, byKey, slot.date);
    const current = weekHours[doctorId]?.total ?? 0;
    const slotHoursFull = slot.hours ?? getHoursForSlot(slot.timeSlot);
    const slotHoursToAdd = (slot.timeSlot === TIME_SLOTS.NOTTE && slot.weekday === 'dom') ? HOURS_NIGHT_START_DAY : slotHoursFull;
    if (current + slotHoursToAdd > debito + 0.01) {
      const reason = debitoBase <= 0
        ? 'Debito orario settimanale 0h: nessun turno clinico assegnabile'
        : `Supera ${debito}h settimanali (attuale: ${current}h, +${slotHoursToAdd}h)`;
      return { ok: false, reason };
    }
  }

  return { ok: true };
}

/**
 * Restituisce i medici assegnabili a uno slot
 */
export function getEligibleDoctors(slot) {
  if (!slot || !slot.date) return [];
  const { doctors } = getState();
  return doctors.filter(d => canAssignDoctorToSlot(d.id, slot).ok);
}
